<template>
  <div :class="$style.base">
    <Content v-if="showRebookSettings" />
    <BaseAccordion
      v-else
      ref="baseAccordion"
      v-model="activeKeys"
      :stayOpen="$screen === 's'"
      :titles="[
        $t('marketing.email.sections.set_up_the_content'),
        $t('marketing.email.sections.when_it_should_be_sent'),
        $t('marketing.email.sections.who_is_going_to_receive_it')
      ]"
    >
      <template #1>
        <Content />
      </template>
      <template #2>
        <WhenToSendEmail
          v-if="
            $route.name === 'waiting-list-template' &&
            unleash.isEnabled('EnableAutomatedWaitingList')
          "
        />
        <ScheduleDelivery
          v-if="showScheduleDelivery"
          v-test="'scheduleDelivery'"
        />
      </template>
      <template #3>
        <Flex direction="column">
          <BaseText>{{ receiveDescription }}</BaseText>
          <Flex
            direction="column"
            v-if="showFilters"
          >
            <BaseRadio
              v-model="selected"
              inline
              :label="$t('marketing.email.sections.who_to_send')"
              :options="sendOptions"
              v-test="'whoToSendSelect'"
            />
            <FilterActions v-show="selected === 'filtered'" />
            <BaseSpinner
              v-show="loadingCustomersCount"
              inline
            />
            <BaseAlert
              v-show="!loadingCustomersCount"
              :text="amountText"
              :color="!filteredCustomersCount ? 'error' : 'primary'"
              v-test="'filteredCustomersCount'"
            />
          </Flex>
        </Flex>
      </template>
    </BaseAccordion>
  </div>
</template>

<script lang="ts">
import { mapState, storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useRoute } from 'vue-router';
import filters from '@/filters';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import { useMarketingStore } from '../../store';
import { useMarketingBuilderStore } from '../../store/builder';
import Content from './Content.vue';
import FilterActions from './FilterActions.vue';
import ScheduleDelivery from './ScheduleDelivery.vue';
import WhenToSendEmail from './WhenToSendEmail.vue';

export default defineComponent({
  components: {
    WhenToSendEmail,
    FilterActions,
    ScheduleDelivery,
    Content,
  },
  props: {
    loadingFilterCount: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const route = useRoute();
    const builderStore = useMarketingBuilderStore();
    const {
      isSubmitted,
      isInvalid,
      allFilters,
      showFilters: showFiltersBar,
    } = storeToRefs(useMarketingStore());
    const { clearFilters } = useMarketingStore();
    const {
      sentDescription,
      receiveDescription,
      showFilters,
      filteredCustomersCount,
      loadingCustomersCount,
      showRebookSettings,
      hideDiscount,
    } = storeToRefs(builderStore);
    return {
      filters,
      unleash,
      route,
      sentDescription,
      receiveDescription,
      showFilters,
      showFiltersBar,
      filteredCustomersCount,
      loadingCustomersCount,
      showRebookSettings,
      hideDiscount,
      isSubmitted,
      isInvalid,
      clearFilters,
      allFilters,
    };
  },
  data() {
    return {
      selected: 'all',
      activeKeys: this.route.name === 'rebook-reminder-template' ? ['2'] : [],
    };
  },
  watch: {
    selected(selected) {
      if (selected === 'all') {
        this.clearFilters();
      }
    },
    activeKeys() {
      if (!this.activeKeys.find((key) => key === '1')) {
        this.isSubmitted = false;
      }
    },
    isSubmitted() {
      if (this.isSubmitted && this.isInvalid) {
        this.showFiltersBar = false;
        this.activeKeys = [...this.activeKeys, '1'];
      }
    },
    allFilters: {
      handler() {
        if (this.allFilters.length) {
          this.selected = 'filtered';
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    showDiscount() {
      return !this.hideDiscount && this.hasFeatureFlag('module-marketing');
    },
    showScheduleDelivery() {
      return this.$route.name.startsWith('newsletter-template');
    },
    amountText() {
      return this.$t(
        'marketing.email.sections.receive_email',
        { amount: this.filteredCustomersCount },
        this.filteredCustomersCount,
      );
    },
    sendOptions() {
      return [
        {
          label: this.$t('marketing.email.sections.all_customers'),
          value: 'all',
        },
        {
          label: this.$t('marketing.email.sections.filtered_customers'),
          value: 'filtered',
        },
      ];
    },
  },
  mounted() {
    if (this.allFilters.length) {
      this.activeKeys = [...this.activeKeys, '3'];
    }
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
  position: relative;
}
</style>
